import { AgGridReactProps } from '@ag-grid-community/react';
import { makeStyles } from '@mui/styles';
import { ClientWalletRoleEnum, TransactionStatusCode, TransactionTypeChar } from '@tassat/common-definitions';
import { UserRecord, UserRole } from '@tassat/fred-definitions';
import { TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import React from 'react';

import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountByUserAccount } from 'Selectors/accounts';
import { getFirmByFirmId } from 'Selectors/firms';
import { getKillSwitchOn } from 'Selectors/killswitch';
import { getUserAccountByTransaction } from 'Selectors/userAccounts';

interface TransactionStatusCellRendererProps extends AgGridReactProps{
    value: number;
    data: TransactionHistoryRecord;
    currentUser: UserRecord;
    currentRole: UserRole;
}

const muiStyles = makeStyles((theme: ApplicationMuiTheme) => ({
    statusText: {
        whiteSpace: 'normal' as const,
        lineHeight: '18px'
    },
    reviewButton: {
        //TODO: New color for warning
        backgroundColor: '#EDCB26',
        padding: '.5rem .75rem',
        borderRadius: '3px',
        lineHeight: '18px'
    },
    failedButton: {
        backgroundColor: theme?.app?.applicationColors.failure ||
            '#FA5252',
        padding: '.5rem .75rem',
        borderRadius: '3px',
        lineHeight: '18px'
    },
    cancelButton: {
        backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter ||
            '#CFCFCF',
        border: `1px solid ${theme?.app?.applicationColors.enumColors.grey ||
        '#888888'}`,
        borderRadius: '3px',
        padding: '.5rem .75rem',
        color: theme?.app?.applicationColors.enumColors.black ||
            '#000',
        lineHeight: '18px'
    }
}));

export const TransactionTypeConversion = (props: TransactionStatusCellRendererProps) => {
    const {
        value,
        data,
        currentUser,
        currentRole
    } = props;
    const killSwitch = useAppSelector(getKillSwitchOn);
    const userAccount = useAppSelector((state) => getUserAccountByTransaction(state, props.data));
    const transactionAccount = useAppSelector((state) => getAccountByUserAccount(state, userAccount));
    const transactionFirm = useAppSelector((state) => getFirmByFirmId(state, transactionAccount?.FirmId || ''));
    const classes = muiStyles(props);

    const canUserApprove = React.useMemo(() => {
        return data.RequesterId !== currentUser.UserId && !currentUser.IsFrozen && userAccount &&
            userAccount.ClientRoleId === ClientWalletRoleEnum.APPROVER &&
            transactionAccount && !transactionAccount.IsLocked &&
            transactionFirm && !transactionFirm.IsFrozen;
    }, [ data, currentUser, transactionFirm, transactionAccount, userAccount ]);

    const showReview = React.useMemo(() => {
        let review = false;

        if (data.Status === TransactionStatusCode.PENDING_APPROVAL && canUserApprove) {
            review = true;
        }

        if (review && killSwitch && data.type !== TransactionTypeChar.send) {
            review = false;
        }

        return review;
    }, [ data, canUserApprove, killSwitch ]);

    const showCancel = React.useMemo(() => {
        let cancel = currentUser?.IsAdmin ?
            data.Status === TransactionStatusCode.PENDING_APPROVAL && !currentUser.IsFrozen &&
            (currentRole.IsApprover && !showReview)
            : data.Status === TransactionStatusCode.PENDING_APPROVAL && !currentUser.IsFrozen &&
                (userAccount?.ClientRoleId === ClientWalletRoleEnum.MAKER ||
                    (userAccount?.ClientRoleId === ClientWalletRoleEnum.APPROVER && data.RequesterId === currentUser.UserId)) &&
                    !transactionAccount?.IsLocked && !transactionFirm?.IsFrozen;

        if (cancel && killSwitch && data.type !== TransactionTypeChar.send) {
            cancel = false;
        }

        return cancel;
    }, [ data, currentUser, transactionFirm, transactionAccount, userAccount, showReview, killSwitch ]);

    const showFailed = React.useMemo(() => {
        let failed = false;

        const canUserApproveFailed = currentUser.IsAdmin &&
            (currentRole.IsTransactionHistoryAllowed);

        if (data.Status === TransactionStatusCode.BANK_NO_ACK && canUserApproveFailed) {
            failed = true;
        }

        return failed;
    }, [ data, currentUser, currentRole ]);

    const displayValue = value || 'Pending';

    return (
        <>
            {showReview ?
                <span className={classes.reviewButton}>Review</span>
                : showCancel ?
                    <span className={classes.cancelButton}>Cancel</span>
                    : showFailed ?
                        <span className={classes.failedButton}>Failed</span>
                        : <span className={classes.statusText}>{displayValue}</span>}
        </>
    );
};

export default TransactionTypeConversion;